import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Trash2, Edit, Calculator } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermission } from "@/hooks/usePermission";

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex flex-col gap-0.5 p-3 rounded-md bg-muted/30 border border-border">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-sm ${mono ? "font-mono tabular-nums" : ""} text-foreground`}>{value || "-"}</span>
    </div>
  );
}

export default function CotizacionesACDetailPage() {
  const { cotId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canExport = usePermission("otac_export");
  const canDelete = usePermission("otac_delete");
  const canEdit = usePermission("otac_edit");

  const { data: cot, isLoading, error } = useQuery({
    queryKey: ["cotizacion-ac", cotId],
    queryFn: async () => (await api.get(`/cotizaciones-ac/${cotId}`)).data,
    enabled: !!cotId,
  });

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/cotizaciones-ac/${cotId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion_${cot?.folio || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al descargar PDF");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/cotizaciones-ac/${cotId}`);
      toast.success("Cotización eliminada");
      qc.invalidateQueries({ queryKey: ["cotizaciones-ac"] });
      navigate("/ots-comercial?tab=cotizador");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !cot) {
    return (
      <div className="text-center py-12">
        <Calculator className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">Cotización no encontrada</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/ots-comercial?tab=cotizador">Regresar a la lista</Link>
        </Button>
      </div>
    );
  }

  const items = cot.items || [];
  const indicaciones = cot.indicaciones || [];

  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="cotizacion-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/ots-comercial?tab=cotizador" data-testid="cotizacion-detail-back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
              Cotización
              <Badge variant="outline" className="font-mono text-base" data-testid="cotizacion-detail-folio">
                {cot.folio}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">Creada el {cot.created_at?.slice(0, 10) || "-"} por {cot.created_by || "-"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" asChild data-testid="cotizacion-detail-edit">
              <Link to={`/ots-comercial/cotizaciones/${cotId}/edit`}>
                <Edit className="h-4 w-4 mr-1" /> Editar
              </Link>
            </Button>
          )}
          {canExport && (
            <Button variant="outline" onClick={downloadPdf} data-testid="cotizacion-detail-pdf">
              <FileText className="h-4 w-4 mr-1" /> Descargar PDF
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="cotizacion-detail-delete-trigger">
                  <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminar cotización</AlertDialogTitle>
                  <AlertDialogDescription>
                    ¿Estás seguro de eliminar <strong>{cot.folio}</strong>? Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} data-testid="cotizacion-detail-delete-confirm">
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Info Grid */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Datos Generales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <InfoRow label="Cliente" value={cot.cliente} />
            <InfoRow label="Contacto" value={cot.contacto} />
            <InfoRow label="Fecha" value={cot.fecha} mono />
            <InfoRow label="Vigencia" value={cot.vigencia} />
            <InfoRow label="Condiciones de Pago" value={cot.condiciones_pago} />
            <InfoRow label="Folio" value={cot.folio} mono />
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Conceptos / Servicios</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-3 w-8">#</th>
                  <th className="py-2.5 px-3">Descripción</th>
                  <th className="py-2.5 px-3 w-24">Unidad</th>
                  <th className="py-2.5 px-3 w-24 text-right">Cantidad</th>
                  <th className="py-2.5 px-3 w-32 text-right">Precio Unitario</th>
                  <th className="py-2.5 px-3 w-32 text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t border-border/60" data-testid={`cotizacion-detail-item-${idx}`}>
                    <td className="py-2.5 px-3 text-center text-muted-foreground">{idx + 1}</td>
                    <td className="py-2.5 px-3">{it.descripcion || "-"}</td>
                    <td className="py-2.5 px-3">{it.unidad || "Pieza"}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums">{it.cantidad || 0}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums">{fmtMoney(it.precio_unitario)}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums font-medium">{fmtMoney(it.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals */}
          <div className="flex justify-end border-t border-border p-4">
            <div className="w-72 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-mono tabular-nums" data-testid="cotizacion-detail-subtotal">{fmtMoney(cot.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">IVA (16%):</span>
                <span className="font-mono tabular-nums" data-testid="cotizacion-detail-iva">{fmtMoney(cot.iva)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold border-t border-border pt-1.5">
                <span>Total:</span>
                <span className="font-mono tabular-nums text-primary" data-testid="cotizacion-detail-total">{fmtMoney(cot.total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicaciones */}
      {indicaciones.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Indicaciones / Incluye</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-1.5" data-testid="cotizacion-detail-indicaciones">
              {indicaciones.map((ind, idx) => (
                <li key={idx} className="text-sm text-foreground/90">{ind}</li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Notas */}
      {cot.notas && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Notas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground/85 whitespace-pre-wrap" data-testid="cotizacion-detail-notas">{cot.notas}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
